import { useMemo } from 'react';
export const DOTS = '...';
//Create an array of certain length and set the elements within it from start value to end value.
const range = (start, end) => {
  let length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};
/**
 * Custom hook for pagination range calculation
 * @param {number} totalCount - total number of items in filteredElements
 * @param {number} pageSize - number of items per page, from config
 * @param {number} siblingCount - number of sibling pages to show on either side of current page
 * @param {number} currentPage - current page number
 * @returns
 */
export const usePagination = ({ totalCount, pageSize, siblingCount = 1, currentPage }) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
    const totalPageNumbers = siblingCount + 5;
    /*
			Case 1:
			If the number of pages is less than the page numbers we want to show in our
			paginationComponent, we return the range [1..totalPageCount]
		*/
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }
    /*
			Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
		*/
    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);
    /*
			We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
		*/
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;
    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;
    /*
			Case 2: No left dots to show, but rights dots to be shown
		*/
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, totalPageCount];
    }
    /*
			Case 3: No right dots to show, but left dots to be shown
		*/
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }
    /*
			Case 4: Both left and right dots to be shown
		*/
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

	 return [];
  }, [totalCount, pageSize, siblingCount, currentPage]);
  return paginationRange;
};
